/**
 * Quest Definitions for Different User Types
 * 
 * This module contains quest definitions tailored to three user types:
 * - Installer: Practical installation, commissioning, and hydraulic balancing
 * - Energy Consultant: Building analysis, COP optimization, cost/benefit analysis
 * - Sales Person: Customer communication, efficiency benefits, value proposition
 * 
 * Quest 1 (System Familiarization) is shared across all user types.
 */

import React from "react";
import {
  ExperimentOutlined,
  ThunderboltOutlined,
  BulbOutlined,
  SafetyCertificateOutlined,
  TrophyOutlined,
  ToolOutlined,
  CalculatorOutlined,
  TeamOutlined,
  DollarOutlined,
  HomeOutlined,
  SettingOutlined,
  CheckSquareOutlined,
  RiseOutlined,
  HeartOutlined
} from "@ant-design/icons";
import {
  GERMAN_SHARED_QUEST_OVERRIDES,
  GERMAN_USER_TYPE_QUEST_OVERRIDES,
} from "./QuestDefinitions.de";

// ============================================================================
// SHARED QUEST: System Familiarization (Same for all user types)
// ============================================================================

const SYSTEM_FAMILIARIZATION_QUEST = {
  id: 1,
  title: "System Familiarization",
  category: "Foundation",
  icon: <ExperimentOutlined />,
  difficulty: "Beginner",
  estimatedTime: "10 minutes",
  learningObjectives: [
    "Understand the heat pump simulation interface layout",
    "Identify key system components: external unit, room unit, and control panel",
    "Observe real-time temperature dynamics and energy flow",
    "Learn to use visualization overlays for radiator rating and heat flux",
    "Understand the valve system for temperature control"
  ],
  scenario: {
    context: "You are learning to operate a modern air-source heat pump system. Before making any adjustments, you need to understand how the system behaves under normal operation.",
    realWorldConnection: "In professional practice, understanding system behavior before making adjustments prevents costly mistakes and ensures safe operation."
  },
  steps: [
    {
      id: "1a",
      task: "Start the simulation",
      instruction: "Locate the play button (▶) in the toolbar at the top of the simulation area and click it to start the heat pump operation.",
      detailedGuidance: "The play button is the leftmost control in the toolbar. When clicked, the simulation begins calculating heat transfer, temperature changes, and system efficiency in real-time. You'll notice temperatures starting to change.",
      successIndicator: "The simulation is running when you see temperature values changing and the external unit showing COP values."
    },
    {
      id: "1b",
      task: "Observe the temperature display",
      instruction: "Watch how the room temperature changes over time. Notice the relationship between heat output (from the radiator) and heat losses (through walls and ventilation).",
      detailedGuidance: "The room unit displays the current temperature. If heat output exceeds heat losses, the temperature rises. If losses exceed output, it falls. This dynamic balance is the core of HVAC system design.",
      successIndicator: "You can see the room temperature value updating as the simulation progresses."
    },
    {
      id: "1c",
      task: "Check the outdoor temperature display",
      instruction: "Find the outdoor temperature indicator in the room visualization. This shows the current external conditions affecting your heating system.",
      detailedGuidance: "The outdoor temperature is displayed in the room area, typically near the wall or window. This value is crucial because the temperature difference between inside and outside (ΔT) drives all heat loss calculations. Colder outside temperatures require more heating power.",
      successIndicator: "You can read the current outdoor temperature value from the room visualization."
    },
    {
      id: "1d",
      task: "Locate the Control Panel",
      instruction: "Find the Control Panel on the right side of the simulation. This is your main interface for adjusting system parameters.",
      detailedGuidance: "The Control Panel contains all adjustable parameters: outdoor temperature/city selection, room type, supply temperature, radiator sizing, U-values for building envelope, and ventilation settings. Mastering this panel is essential for system optimization.",
      successIndicator: "You can identify the Control Panel and its main sections."
    },
    {
      id: "1e",
      task: "Identify the COP display",
      instruction: "Find the Coefficient of Performance (COP) indicator on the external unit. This shows how efficiently the heat pump is converting electrical energy to heat.",
      detailedGuidance: "COP is displayed on the external (outdoor) unit visualization. A COP of 3.0 means for every 1 kW of electricity consumed, the system delivers 3 kW of heat. Higher COP = better efficiency.",
      successIndicator: "You can locate and read the COP value on the external unit display."
    },
    {
      id: "1f",
      task: "Enable the radiator rating overlay",
      instruction: "Click the radiator rating toggle button in the toolbar to display the radiator's heat output capacity and current performance.",
      detailedGuidance: "The radiator rating overlay shows the radiator's nominal heat output (in Watts) and how it varies with supply temperature. This visualization helps you understand the relationship between radiator size, water temperature, and heat delivery capacity. Higher supply temperatures increase output, but reduce heat pump efficiency.",
      successIndicator: "You can see the radiator rating information displayed on or near the radiator visualization."
    },
    {
      id: "1g",
      task: "Explore the heat flux overlay",
      instruction: "Click the heat flux toggle button in the toolbar to visualize heat flow through the building envelope.",
      detailedGuidance: "The heat flux overlay shows colored arrows representing heat transfer. Red/orange arrows indicate heat escaping from the building to the outside. The size and color intensity of arrows correspond to the magnitude of heat loss. This visualization helps identify thermal weak points.",
      successIndicator: "You can see colored arrows showing heat flow direction and intensity on the room visualization."
    },
    {
      id: "1h",
      task: "Understand the valve system",
      instruction: "Locate the predefined valve and the thermostat on the radiator. These components control water flow and room temperature.",
      detailedGuidance: "The valve system has two parts: (1) The preset valve (orange dial) limits maximum flow for hydraulic balancing, and (2) The thermostat head (white) automatically adjusts flow based on room temperature. Together they ensure efficient, comfortable heating.",
      successIndicator: "You can identify both the preset valve (flow limiter) and the thermostat head on the radiator."
    }
  ],
  completionCriteria: {
    description: "Start the simulation and observe system operation",
    conditions: ["Simulation must be running"]
  },
  knowledgeSynthesis: {
    keyTakeaways: [
      "Heat pump systems continuously balance heat input against heat losses",
      "COP (Coefficient of Performance) measures energy efficiency",
      "Real-time observation is essential before making system adjustments",
      "The radiator rating overlay shows heat output capacity at different temperatures",
      "The heat flux overlay helps visualize where heat is escaping the building",
      "Thermostat valves combine preset flow limiting with automatic temperature control"
    ],
    formulaHighlight: "COP = Q_heat / W_electrical — where Q is heat delivered and W is electrical energy consumed",
    practicalTip: "In field practice, always run a system for 15-20 minutes before taking measurements to ensure steady-state operation."
  }
};

// ============================================================================
// INSTALLER QUESTS
// ============================================================================

export const INSTALLER_QUESTS = [
  SYSTEM_FAMILIARIZATION_QUEST,
  {
    id: 2,
    title: "Design Conditions Setup",
    category: "Installation Basics",
    icon: <SafetyCertificateOutlined />,
    difficulty: "Beginner",
    estimatedTime: "8 minutes",
    learningObjectives: [
      "Understand reference outdoor temperatures for HVAC design (DIN EN 12831)",
      "Learn standardized indoor comfort temperatures for different room types",
      "Apply building codes to heating system installation"
    ],
    scenario: {
      context: "You are installing a heating system for a residential building. The system must maintain comfort during the coldest expected conditions while meeting DIN EN 12831 standards.",
      realWorldConnection: "Installers must understand design conditions to properly size and configure systems. Using incorrect values leads to customer complaints and callbacks."
    },
    steps: [
      {
        id: "2a",
        task: "Set the design outdoor temperature",
        instruction: "In the Control Panel, set the outdoor temperature to -10°C or select a city to use its reference design temperature.",
        detailedGuidance: "The design outdoor temperature (Normaußentemperatur) is the coldest temperature a heating system must handle. Each region has its own value defined in DIN EN 12831 — for example, -10°C for moderate climates or colder for mountain regions.",
        successIndicator: "Outdoor temperature displays -10°C or lower."
      },
      {
        id: "2b",
        task: "Configure the room type",
        instruction: "Select 'Living Room' as the room type to apply the standard comfort temperature of 20°C.",
        detailedGuidance: "DIN EN 12831 specifies different design indoor temperatures: Living rooms 20°C, bathrooms 24°C, bedrooms 18°C, and corridors 15°C. The room type selector automatically sets the appropriate target temperature.",
        successIndicator: "Room type shows 'Living Room' and target temperature is 20°C."
      },
      {
        id: "2c",
        task: "Understand the design temperature difference",
        instruction: "With outdoor at -10°C and indoor at 20°C, note that ΔT = 30K. This determines the heating load for system sizing.",
        detailedGuidance: "The temperature difference (ΔT = T_indoor - T_outdoor) drives all heat loss calculations. At ΔT = 30K, you're at maximum design conditions. This is critical for proper system sizing.",
        successIndicator: "You understand that ΔT = 20°C - (-10°C) = 30K is the design condition."
      }
    ],
    completionCriteria: {
      description: "Configure reference conditions per DIN EN 12831",
      conditions: [
        "Outdoor temperature ≤ -10°C",
        "Room type set to 'Living Room'",
        "Target temperature = 20°C"
      ]
    },
    knowledgeSynthesis: {
      keyTakeaways: [
        "Design temperatures are standardized in building codes (DIN EN 12831)",
        "Reference outdoor temperature varies by geographic location",
        "Indoor comfort temperature depends on room function",
        "The temperature difference (ΔT) drives heat loss calculations"
      ],
      formulaHighlight: "ΔT = T_indoor - T_outdoor — The design temperature difference determines heating load",
      practicalTip: "Always check the local design outdoor temperature from official sources before sizing a system. Regional variations matter!"
    }
  },
  {
    id: 3,
    title: "Emitter Upgrade for Heat Pump Retrofit",
    category: "Practical Installation",
    icon: <ToolOutlined />,
    difficulty: "Intermediate",
    estimatedTime: "18 minutes",
    learningObjectives: [
      "Understand the dramatic COP difference between boiler temperatures (75°C) and heat pump temperatures (45°C)",
      "Determine if a given radiator type can deliver enough heat at low supply temperatures",
      "Compare Type 21 vs Type 22 vs Type 33 radiators for heat pump compatibility",
      "Use Q-net to verify whether the emitter meets heating demand",
      "Make informed emitter upgrade recommendations based on COP improvement"
    ],
    scenario: {
      context: "A homeowner is planning to replace their gas boiler with a heat pump. They currently have Type 21 panel radiators (Plattenheizkörper) and want to know: Can they achieve a supply temperature of 45°C with their existing radiators? If not, which emitter upgrade (Type 22, Type 33, or larger size) would be needed?",
      realWorldConnection: "This is the #1 question installers face in retrofit projects. The answer determines project cost and customer satisfaction. Getting it wrong means either overspending on unnecessary upgrades or underperforming systems that frustrate homeowners."
    },
    steps: [
      {
        id: "3a",
        task: "Set up realistic building conditions",
        instruction: "Configure the simulation for a typical retrofit scenario: Set outdoor temperature to -10°C (design conditions), and select Living Room (20°C target). Set the U-value to 0.4 W/(m²K) for a well-insulated building.",
        detailedGuidance: "These represent a typical German building that has been partially renovated with improved insulation. The U-value of 0.4 represents good thermal performance achievable through window upgrades and added wall/roof insulation.",
        successIndicator: "Outdoor temp is -10°C, target is 20°C, U-value is around 0.4."
      },
      {
        id: "3b",
        task: "Start with existing Type 21 radiator",
        instruction: "In the Radiator section, select Type 21 and a medium size (80 cm length). Note the nominal power rating shown at 75/65°C conditions.",
        detailedGuidance: "Type 21 radiators have one convector plate behind two heating panels. The nominal power (e.g., ~1000W at 75/65/20°C) is tested under high-temperature conditions that don't reflect heat pump operation.",
        successIndicator: "Type 21 selected, you can see the nominal power value."
      },
      {
        id: "3c",
        task: "Set supply temperature to 75°C (boiler mode)",
        instruction: "In the Supply Temperature field, enter 75°C. This represents the typical operating temperature of a gas boiler system.",
        detailedGuidance: "At 75°C supply temperature, the radiator delivers its rated output. This is how the existing system operates with the gas boiler. We'll use this as our baseline to compare efficiency.",
        successIndicator: "Supply temperature is set to 75°C."
      },
      {
        id: "3d",
        task: "Record the COP at boiler temperatures",
        instruction: "Look at the COP value displayed on the external unit. Write down this value — this is the efficiency a heat pump would have if forced to run at boiler temperatures.",
        detailedGuidance: "At 75°C supply with -10°C outdoor, the COP will be very low (around 2.0-2.5). This shows why running a heat pump at boiler temperatures wastes energy. Remember this value for comparison later!",
        successIndicator: "You've noted the COP value at 75°C supply temperature."
      },
      {
        id: "3e",
        task: "Set the target supply temperature to 45°C",
        instruction: "Now change the supply temperature to 45°C. This is the target temperature the homeowner wants to achieve for efficient heat pump operation.",
        detailedGuidance: "45°C is a good target for heat pump retrofits — it provides a balance between efficiency (COP ~4.0) and practical radiator sizing. Lower is better, but requires larger emitters.",
        successIndicator: "Supply temperature is set to 45°C."
      },
      {
        id: "3f",
        task: "Check Q-net to see if Type 21 is sufficient",
        instruction: "Look at the Q-net value displayed in the Radiator section. If Q-net is NEGATIVE (shown in red), the radiator cannot deliver enough heat at 45°C supply temperature.",
        detailedGuidance: "Q-net = Heat Output − Heat Losses. A negative Q-net means the radiator is undersized for 45°C operation. The room will not reach the target temperature. This tells you the existing Type 21 radiator needs to be upgraded.",
        successIndicator: "You can see the Q-net value and understand if it's positive or negative."
      },
      {
        id: "3g",
        task: "Upgrade to Type 22 and check again",
        instruction: "Change the radiator type to Type 22 (keeping the same size at 80 cm). Check if Q-net improves — is it now positive?",
        detailedGuidance: "Type 22 has two convector plates and two panels, giving approximately 30% more heat output at the same supply temperature. Watch how P-design increases and Q-net changes.",
        successIndicator: "Type 22 selected, you observe the change in heat output and Q-net."
      },
      {
        id: "3h",
        task: "Try Type 33 if needed",
        instruction: "If Q-net is still negative with Type 22, switch to Type 33 radiator and check if this achieves positive Q-net at 45°C.",
        detailedGuidance: "Type 33 (three panels, three convectors) provides the maximum heat output from panel radiators of the same size. If even Type 33 shows negative Q-net, you'll need to increase radiator length.",
        successIndicator: "Type 33 selected, you can see if Q-net is now positive."
      },
      {
        id: "3i",
        task: "Find the minimum configuration for positive Q-net",
        instruction: "Experiment with radiator type and size combinations until you find the MINIMUM upgrade that achieves positive Q-net at 45°C supply. Try increasing length (90 cm, 100 cm) if the type alone isn't enough.",
        detailedGuidance: "The goal is to find the most cost-effective upgrade: Type 22 same size? Type 33 same size? Type 22 larger size? Document your recommendation — the smallest upgrade that works is usually the best value.",
        successIndicator: "Q-net is positive (≥0) at 45°C supply temperature."
      },
      {
        id: "3j",
        task: "Compare COP improvement and document your recommendation",
        instruction: "With your final configuration at 45°C, note the COP on the external unit. Compare this to the COP you recorded at 75°C in step 3d. Calculate the efficiency gain!",
        detailedGuidance: "You should see the COP has improved dramatically — from ~2.0-2.5 at 75°C to ~4.0+ at 45°C. This means the heat pump now produces nearly TWICE as much heat per unit of electricity! This efficiency gain is the payoff for upgrading the radiators.",
        successIndicator: "COP ≥ 4.0 at 45°C. You can quantify the improvement compared to 75°C operation."
      }
    ],
    completionCriteria: {
      description: "Find an emitter configuration that enables 45°C supply temperature operation",
      conditions: [
        "Supply temperature ≤ 45°C",
        "Heat output ≥ 90% of total heat loss (Q-net positive or near-zero)"
      ]
    },
    knowledgeSynthesis: {
      keyTakeaways: [
        "COP nearly doubles when reducing supply temperature from 75°C to 45°C — this is the main benefit of emitter upgrades",
        "Q-net tells you instantly if an emitter can meet heating demand at a given supply temperature",
        "Negative Q-net = radiator too small, positive Q-net = system can maintain target temperature",
        "Type 22 provides ~30% more output than Type 21; Type 33 provides ~40% more than Type 22",
        "The radiator upgrade cost is quickly recovered through improved COP and lower electricity bills"
      ],
      formulaHighlight: "COP at 75°C ≈ 2.0-2.5 vs COP at 45°C ≈ 4.0+. That's nearly 50% less electricity for the same heat output!",
      practicalTip: "When presenting to customers, always show the COP comparison: 'With your current radiators at boiler temperatures, you'd get COP 2.5. With upgraded radiators at 45°C, you'll get COP 4.0 — that's 40% lower running costs!'"
    }
  },
  {
    id: 4,
    title: "Hydraulic Balancing",
    category: "Commissioning",
    icon: <SettingOutlined />,
    difficulty: "Intermediate",
    estimatedTime: "12 minutes",
    learningObjectives: [
      "Master hydraulic balancing for optimal system performance",
      "Learn to set preset valves for proper flow distribution",
      "Understand the relationship between flow rate and heat transfer"
    ],
    scenario: {
      context: "You've installed a heat pump system and now need to commission it. The system has multiple radiators that need to be balanced to ensure even heat distribution and minimum pump energy.",
      realWorldConnection: "Hydraulic balancing is often skipped but it's crucial for system efficiency and customer comfort. A well-balanced system reduces pump energy by 50-80%."
    },
    steps: [
      {
        id: "4a",
        task: "Observe current valve position",
        instruction: "Note the current preset valve position (shown on the orange dial). It may be fully open at position 7.",
        detailedGuidance: "The preset valve has 7 positions, from 1 (most restricted) to 7 (fully open). Higher positions allow more flow but may not improve heat output due to diminishing returns.",
        successIndicator: "You can identify the current valve position."
      },
      {
        id: "4b",
        task: "Reduce valve preset gradually",
        instruction: "Click on the preset valve selector and reduce it step by step. Watch the heat output as you reduce flow.",
        detailedGuidance: "As you reduce flow, return temperature drops (larger ΔT). As long as heat output ≥ heat loss, the room will maintain temperature with lower pump energy.",
        successIndicator: "You've experimented with different valve positions."
      },
      {
        id: "4c",
        task: "Find the optimal setting",
        instruction: "Find the lowest valve setting (position 3 or lower) that still maintains adequate heat output.",
        detailedGuidance: "The goal is minimum flow that still meets heat demand. Lower flow = less pump energy, less noise, and often better efficiency.",
        successIndicator: "Valve position ≤ 3 while heat output remains adequate."
      },
      {
        id: "4d",
        task: "Verify heat delivery",
        instruction: "Confirm that heat output still exceeds 90% of total heat loss at the optimized valve setting.",
        detailedGuidance: "A small reduction in heat delivery capacity is acceptable because real-world operation rarely sees design conditions continuously.",
        successIndicator: "Heat output ≥ 90% of total heat loss with valve position ≤ 3."
      }
    ],
    completionCriteria: {
      description: "Balance the system for minimum flow rate while meeting heat demand",
      conditions: [
        "Valve position ≤ 3/7 (position 3 or lower)",
        "Heat output ≥ 90% of total heat loss"
      ]
    },
    knowledgeSynthesis: {
      keyTakeaways: [
        "Hydraulic balancing can reduce pump energy by 50-80%",
        "Lower flow creates larger supply-return ΔT",
        "Properly balanced systems are quieter and more efficient",
        "Balance ensures even heat distribution in multi-radiator systems"
      ],
      formulaHighlight: "Q = ṁ × c_p × ΔT — Double ΔT → Half the flow for same Q",
      practicalTip: "Always balance the system during commissioning. It takes 30 minutes but the customer will get lower bills and better comfort."
    }
  },
  {
    id: 5,
    title: "Ventilation Impact Assessment",
    category: "System Performance",
    icon: <BulbOutlined />,
    difficulty: "Intermediate",
    estimatedTime: "10 minutes",
    learningObjectives: [
      "Understand ventilation heat losses and their impact on system performance",
      "Learn to assess if a building has excessive air infiltration",
      "Advise customers on ventilation improvements"
    ],
    scenario: {
      context: "A customer complains that their new heat pump struggles to maintain temperature. You suspect excessive ventilation or air leakage is the problem.",
      realWorldConnection: "Many installer callbacks are actually ventilation issues, not heat pump problems. Being able to diagnose this saves time and builds customer trust."
    },
    steps: [
      {
        id: "5a",
        task: "Check current ventilation settings",
        instruction: "Observe the ventilation loss value displayed in the simulation. Note how it compares to envelope losses.",
        detailedGuidance: "In well-insulated buildings, ventilation can be 40-60% of total heat loss. If ventilation losses are very high, the building may have air leakage issues.",
        successIndicator: "You can see and compare ventilation vs envelope losses."
      },
      {
        id: "5b",
        task: "Simulate excessive ventilation",
        instruction: "Set the air change rate to 2.0 ACH or higher. Observe how the system struggles.",
        detailedGuidance: "At 2.0+ ACH, the building is very leaky. Even a large heat pump may not maintain temperature. This helps you recognize the symptoms of ventilation problems.",
        successIndicator: "You observe high ventilation losses at 2.0 ACH."
      },
      {
        id: "5c",
        task: "Reduce to normal levels",
        instruction: "Reduce air change rate to below 1.25 ACH and observe the improvement.",
        detailedGuidance: "Typical modern buildings should be 0.5-1.0 ACH. Older buildings are often 1.5-2.0 ACH due to poor sealing. The difference in heat demand is dramatic.",
        successIndicator: "Air change rate < 1.25 ACH and system maintains temperature easily."
      },
      {
        id: "5d",
        task: "Calculate the energy impact",
        instruction: "Note the reduction in ventilation heat loss when ACH drops. This shows the customer the benefit of air sealing.",
        detailedGuidance: "Halving ACH halves ventilation losses. For a typical home, this could be 1-2 kW reduction in heat demand — significant savings!",
        successIndicator: "You can quantify the ventilation loss reduction."
      }
    ],
    completionCriteria: {
      description: "Optimize ventilation for efficiency",
      conditions: ["Air change rate < 1.25 ACH"]
    },
    knowledgeSynthesis: {
      keyTakeaways: [
        "Ventilation is often the largest heat loss in modern buildings",
        "Excessive ACH indicates air leakage problems",
        "Air sealing is often cheaper than a larger heat pump",
        "Recommend HRV systems for airtight buildings"
      ],
      formulaHighlight: "Q_vent = (ρ × c_p × V × ACH × ΔT) / 3600 — Ventilation heat loss",
      practicalTip: "If a heat pump isn't keeping up, check for obvious air leaks: drafty windows, unsealed penetrations, or missing insulation in the loft."
    }
  },
  {
    id: 6,
    title: "Commissioning Verification",
    category: "Quality Assurance",
    icon: <CheckSquareOutlined />,
    difficulty: "Advanced",
    estimatedTime: "15 minutes",
    learningObjectives: [
      "Perform a complete system commissioning check",
      "Verify system meets design specifications",
      "Document performance for customer handover"
    ],
    scenario: {
      context: "You've completed a heat pump installation and need to perform final commissioning checks before handing over to the customer. The system must achieve design performance.",
      realWorldConnection: "Proper commissioning documentation protects you legally and ensures customer satisfaction. It's the mark of a professional installer."
    },
    steps: [
      {
        id: "6a",
        task: "Set reference conditions",
        instruction: "Configure the simulation for design conditions: outdoor -10°C, living room at 20°C target.",
        detailedGuidance: "These are the conditions your system was designed for. If it works here, it will work even better in milder weather.",
        successIndicator: "Design conditions are set correctly."
      },
      {
        id: "6b",
        task: "Verify heat output meets demand",
        instruction: "Confirm that heat output exceeds total heat loss (envelope + ventilation) at design conditions.",
        detailedGuidance: "The system should have capacity to spare at design conditions. If output barely equals losses, consider if equipment is undersized.",
        successIndicator: "Heat output > total heat loss."
      },
      {
        id: "6c",
        task: "Check supply temperature",
        instruction: "Verify supply temperature is 40°C or below for efficient operation.",
        detailedGuidance: "Higher supply temperatures waste energy. If the system needs >45°C to meet demand, the emitters may be undersized.",
        successIndicator: "Supply temperature ≤ 40°C."
      },
      {
        id: "6d",
        task: "Verify COP performance",
        instruction: "Check that COP is 4.5 or higher at design conditions. This confirms efficient system design.",
        detailedGuidance: "COP 4.5+ at ΔT=30K is excellent. COP below 3.5 suggests problems with supply temperature or system sizing.",
        successIndicator: "COP ≥ 4.5."
      },
      {
        id: "6e",
        task: "Final temperature stability",
        instruction: "Confirm room temperature is within 2°C of setpoint, indicating stable operation.",
        detailedGuidance: "Temperature stability confirms the system has adequate capacity and control. Large deviations indicate problems to investigate.",
        successIndicator: "Room temperature within 2°C of target."
      }
    ],
    completionCriteria: {
      description: "Complete commissioning verification",
      conditions: [
        "COP ≥ 4.5",
        "Room temperature within 2°C of setpoint"
      ]
    },
    knowledgeSynthesis: {
      keyTakeaways: [
        "Commissioning verifies design specifications are met",
        "COP 4.5+ at design conditions indicates professional installation",
        "Document all commissioning values for customer handover",
        "Temperature stability confirms proper system operation"
      ],
      formulaHighlight: "COP_system = Q_delivered / W_electrical — Target: COP ≥ 4.5 at design conditions",
      practicalTip: "Create a commissioning checklist you use on every job. Photograph key readings for your records and provide a commissioning certificate to the customer."
    },
    isChallenge: true
  }
];

// ============================================================================
// ENERGY CONSULTANT QUESTS
// ============================================================================

export const ENERGY_CONSULTANT_QUESTS = [
  SYSTEM_FAMILIARIZATION_QUEST,
  {
    id: 2,
    title: "Building Energy Standards",
    category: "Regulatory Framework",
    icon: <SafetyCertificateOutlined />,
    difficulty: "Beginner",
    estimatedTime: "8 minutes",
    learningObjectives: [
      "Understand DIN EN 12831 design conditions",
      "Learn how design temperatures affect heat load calculations",
      "Apply building codes to energy assessments"
    ],
    scenario: {
      context: "You are conducting an energy assessment for a client considering heating system upgrade. You need to establish proper design conditions according to standards.",
      realWorldConnection: "Energy consultants must base recommendations on recognized standards. This ensures credibility and compliance with regulations."
    },
    steps: [
      {
        id: "2a",
        task: "Set design outdoor temperature",
        instruction: "Set the outdoor temperature to -10°C (or your regional reference) to establish design conditions.",
        detailedGuidance: "The design outdoor temperature per DIN EN 12831 varies by location. -10°C is typical for moderate European climates. Mountain regions require -15°C or colder.",
        successIndicator: "Outdoor temperature ≤ -10°C."
      },
      {
        id: "2b",
        task: "Configure room standards",
        instruction: "Select 'Living Room' to apply the 20°C standard comfort temperature.",
        detailedGuidance: "DIN EN 12831 specifies: Living rooms 20°C, bathrooms 24°C, bedrooms 18°C. These are the basis for heating load calculations in energy assessments.",
        successIndicator: "Room type is 'Living Room' with 20°C target."
      },
      {
        id: "2c",
        task: "Calculate design ΔT",
        instruction: "Note that with outdoor -10°C and indoor 20°C, ΔT = 30K. This is your design temperature difference.",
        detailedGuidance: "All heat loss calculations scale with ΔT. Design at 30K means peak heating demand. Milder weather (ΔT = 15K) requires only half the heating power.",
        successIndicator: "You understand the 30K design temperature difference."
      }
    ],
    completionCriteria: {
      description: "Establish standard design conditions",
      conditions: [
        "Outdoor temperature ≤ -10°C",
        "Room type set to 'Living Room'",
        "Target temperature = 20°C"
      ]
    },
    knowledgeSynthesis: {
      keyTakeaways: [
        "Design conditions are standardized in DIN EN 12831",
        "Regional climate determines design outdoor temperature",
        "Indoor temperatures depend on room function",
        "ΔT directly drives heat loss magnitude"
      ],
      formulaHighlight: "ΔT = T_indoor - T_outdoor — The foundation of load calculations",
      practicalTip: "When presenting to clients, explain that proper design conditions ensure the system works even during the coldest weather — building for the worst case."
    }
  },
  {
    id: 3,
    title: "Building Envelope Analysis",
    category: "Energy Assessment",
    icon: <HomeOutlined />,
    difficulty: "Intermediate",
    estimatedTime: "12 minutes",
    learningObjectives: [
      "Master U-value analysis for building components",
      "Calculate transmission heat losses accurately",
      "Identify envelope improvement opportunities"
    ],
    scenario: {
      context: "You are assessing an older building for heat pump suitability. The building envelope needs analysis to determine if insulation upgrades are required before or alongside the heat pump installation.",
      realWorldConnection: "Envelope improvements often provide better ROI than just installing a bigger heat pump. Energy consultants must quantify this trade-off."
    },
    steps: [
      {
        id: "3a",
        task: "Access building envelope parameters",
        instruction: "Click 'U-Parameters' to open the building envelope configuration dialog.",
        detailedGuidance: "The U-Parameters modal allows configuration of walls, roof, and windows. Each contributes to total transmission losses.",
        successIndicator: "U-Parameters dialog is open."
      },
      {
        id: "3b",
        task: "Configure modern insulation standards",
        instruction: "Set wall area to 50 m² and wall U-value to 0.2 W/(m²·K) — representing modern insulation.",
        detailedGuidance: "Modern standards require U ≈ 0.2 W/(m²·K) for walls. Older buildings might have U = 1.0-1.5. The difference in heat loss is 5-7×!",
        successIndicator: "Wall area ~50 m² and U-value ~0.2 W/(m²·K)."
      },
      {
        id: "3c",
        task: "Calculate wall heat loss",
        instruction: "Using Q = U × A × ΔT with U=0.2, A=50 m², ΔT=30K, calculate 300 W wall loss.",
        detailedGuidance: "Q = 0.2 × 50 × 30 = 300 W. This is the transmission loss through walls at design conditions. Compare to the simulation display.",
        successIndicator: "You can calculate transmission losses accurately."
      },
      {
        id: "3d",
        task: "Enable heat flux visualization",
        instruction: "Toggle the heat flux overlay to see where heat is escaping the building.",
        detailedGuidance: "The visualization shows heat flow magnitude. Larger arrows = more heat loss. Windows typically show largest arrows due to higher U-values.",
        successIndicator: "Heat flux overlay is active."
      }
    ],
    completionCriteria: {
      description: "Configure well-insulated building envelope",
      conditions: [
        "Wall area between 48-52 m²",
        "Wall U-value between 0.18-0.22 W/(m²·K)"
      ]
    },
    knowledgeSynthesis: {
      keyTakeaways: [
        "U-value measures thermal transmittance — lower is better",
        "R-value = 1/U — thermal resistance, higher is better",
        "Heat loss = U × A × ΔT — the fundamental equation",
        "Envelope improvements reduce peak demand and enable low-temp operation"
      ],
      formulaHighlight: "Q = U × A × ΔT — Transmission heat loss calculation\nR = 1/U — Thermal resistance",
      practicalTip: "Always calculate the cost-benefit of insulation vs. larger heat pump. Often, insulation provides payback within 5-7 years and lasts 30+ years."
    }
  },
  {
    id: 4,
    title: "Ventilation Energy Impact",
    category: "Energy Analysis",
    icon: <BulbOutlined />,
    difficulty: "Intermediate",
    estimatedTime: "10 minutes",
    learningObjectives: [
      "Quantify ventilation heat losses accurately",
      "Understand ACH and its energy implications",
      "Evaluate heat recovery ventilation benefits"
    ],
    scenario: {
      context: "Your client has a well-insulated building but high energy bills. You suspect ventilation is the main heat loss pathway and need to analyze and recommend solutions.",
      realWorldConnection: "In modern, well-insulated buildings, ventilation often accounts for 50-60% of total heat loss. HRV systems can recover 70-90% of this."
    },
    steps: [
      {
        id: "4a",
        task: "Assess ventilation losses",
        instruction: "Observe the ventilation loss vs envelope loss in the simulation. Note the proportion.",
        detailedGuidance: "In well-insulated buildings (low U-values), ventilation dominates. The ventilation loss formula is: Q = ρ × c_p × V × ACH × ΔT / 3600.",
        successIndicator: "You can identify ventilation as a major heat loss component."
      },
      {
        id: "4b",
        task: "Model different ACH scenarios",
        instruction: "Adjust air change rate to see the impact. Try 2.0 ACH (leaky) vs 0.5 ACH (tight).",
        detailedGuidance: "ACH varies dramatically by building age and type: Passive houses 0.3-0.5, modern tight 0.5-1.0, standard 1.0-1.5, old/leaky 2.0+.",
        successIndicator: "You understand how ACH affects heat loss."
      },
      {
        id: "4c",
        task: "Set optimal ACH",
        instruction: "Set air change rate to below 1.25 ACH representing reasonable modern construction.",
        detailedGuidance: "Below 1.25 ACH is achievable with basic air sealing. Below 0.5 ACH typically requires mechanical ventilation with heat recovery.",
        successIndicator: "ACH < 1.25."
      },
      {
        id: "4d",
        task: "Calculate HRV potential savings",
        instruction: "If HRV recovers 80% of ventilation heat loss, calculate the savings potential.",
        detailedGuidance: "With HRV at 80% efficiency, effective ventilation loss becomes only 20% of the original. For 1 kW ventilation loss, HRV saves 800 W continuously!",
        successIndicator: "You can quantify HRV benefits."
      }
    ],
    completionCriteria: {
      description: "Optimize ventilation in analysis",
      conditions: ["Air change rate < 1.25 ACH"]
    },
    knowledgeSynthesis: {
      keyTakeaways: [
        "Ventilation is often the largest loss in insulated buildings",
        "ACH reduction has linear effect on ventilation losses",
        "HRV systems recover 70-90% of ventilation heat",
        "Balance air quality needs with energy efficiency"
      ],
      formulaHighlight: "Q_vent = (ρ × c_p × V × ACH × ΔT) / 3600\nHRV_savings = Q_vent × η_recovery",
      practicalTip: "For clients with ACH > 1.5, air sealing is usually the best first investment. For clients with ACH < 0.8, recommend HRV for both air quality and energy recovery."
    }
  },
  {
    id: 5,
    title: "COP Optimization Analysis",
    category: "Efficiency Optimization",
    icon: <RiseOutlined />,
    difficulty: "Advanced",
    estimatedTime: "15 minutes",
    learningObjectives: [
      "Master the relationship between supply temperature and COP",
      "Quantify efficiency gains from system optimization",
      "Develop optimal system configurations for clients"
    ],
    scenario: {
      context: "Your client wants to minimize operating costs for their heat pump. You need to analyze how different supply temperatures affect COP and present optimization strategies.",
      realWorldConnection: "Energy consultants who can quantify COP improvements add significant value to client relationships. The operating cost difference over 20 years is substantial."
    },
    steps: [
      {
        id: "5a",
        task: "Establish baseline COP",
        instruction: "Set supply temperature to 55°C (traditional boiler temperature) and note the COP.",
        detailedGuidance: "At 55°C supply with -10°C outdoor, expect COP around 2.5-3.0. This is the typical performance of an un-optimized retrofit.",
        successIndicator: "You've recorded baseline COP at 55°C supply."
      },
      {
        id: "5b",
        task: "Lower supply temperature",
        instruction: "Reduce supply temperature to 40°C and observe COP improvement.",
        detailedGuidance: "Lower supply = smaller temperature lift = higher COP. The Carnot formula shows: COP_ideal = T_hot / (T_hot - T_cold). Lower T_hot dramatically improves efficiency.",
        successIndicator: "Supply temperature is 40°C."
      },
      {
        id: "5c",
        task: "Compensate with radiator sizing",
        instruction: "If heat output drops below demand, increase radiator size to compensate.",
        detailedGuidance: "Lower supply temperature reduces radiator output. Compensate with larger emitters. The extra capital cost is repaid through higher efficiency.",
        successIndicator: "Heat output ≥ 90% of heat loss at 40°C supply."
      },
      {
        id: "5d",
        task: "Calculate annual savings",
        instruction: "Compare COP at 55°C vs 40°C. Calculate the energy cost difference over a heating season.",
        detailedGuidance: "If COP improves from 3.0 to 4.5, electricity use drops by 33%. For a house using 15,000 kWh heat/year, that's 2,000+ kWh electricity saved annually.",
        successIndicator: "You can quantify the efficiency improvement."
      },
      {
        id: "5e",
        task: "Achieve target COP",
        instruction: "Optimize the system to achieve COP of 4.5 or higher.",
        detailedGuidance: "COP 4.5+ at design conditions represents excellent optimization. Present this as a key achievement to clients.",
        successIndicator: "COP ≥ 4.5."
      }
    ],
    completionCriteria: {
      description: "Achieve optimized COP performance",
      conditions: [
        "Supply temperature ≤ 40°C",
        "Heat output ≥ 90% of total heat loss"
      ]
    },
    knowledgeSynthesis: {
      keyTakeaways: [
        "Every degree lower on supply improves COP by 2-3%",
        "COP 4.5 vs 3.0 represents 33% less electricity use",
        "Lower supply requires larger emitters — calculate the trade-off",
        "Present efficiency improvements in annual cost savings"
      ],
      formulaHighlight: "COP_carnot = T_hot / (T_hot - T_cold)\nSavings = Annual_heat × (1/COP_old - 1/COP_new) × €/kWh",
      practicalTip: "Create a simple spreadsheet showing 20-year operating costs at different COP values. The visual impact helps clients understand the value of optimization."
    }
  },
  {
    id: 6,
    title: "Complete System Assessment",
    category: "Professional Analysis",
    icon: <TrophyOutlined />,
    difficulty: "Expert",
    estimatedTime: "20 minutes",
    learningObjectives: [
      "Integrate all analysis factors into comprehensive assessment",
      "Develop client recommendations with ROI analysis",
      "Demonstrate professional-level energy consulting"
    ],
    scenario: {
      context: "A client wants a complete heat pump feasibility assessment for their property. You need to analyze envelope, ventilation, supply temperature, and emitter requirements to provide professional recommendations.",
      realWorldConnection: "This comprehensive approach is what separates professional energy consultants from simple equipment salespeople. Clients value thorough analysis."
    },
    steps: [
      {
        id: "6a",
        task: "Set reference conditions",
        instruction: "Configure -10°C outdoor, Living Room at 20°C for standard assessment conditions.",
        detailedGuidance: "All assessments should start from standard conditions for comparability and code compliance.",
        successIndicator: "Reference conditions are set."
      },
      {
        id: "6b",
        task: "Optimize building envelope",
        instruction: "Set envelope parameters representing good insulation: wall U ≤ 0.3 W/(m²·K).",
        detailedGuidance: "A good envelope is the foundation of efficient heat pump operation. Note the required improvements for existing buildings.",
        successIndicator: "Envelope U-values represent good insulation."
      },
      {
        id: "6c",
        task: "Set appropriate ventilation",
        instruction: "Configure ACH below 1.5 for realistic modern building performance.",
        detailedGuidance: "If the real building has higher ACH, recommend air sealing. If lower, consider HRV requirements.",
        successIndicator: "ACH is appropriate for modern building."
      },
      {
        id: "6d",
        task: "Optimize supply temperature",
        instruction: "Achieve supply temperature ≤ 40°C with appropriate emitter sizing.",
        detailedGuidance: "This may require radiator upgrades or underfloor heating zones. Document the requirements.",
        successIndicator: "Supply temperature ≤ 40°C."
      },
      {
        id: "6e",
        task: "Achieve target efficiency",
        instruction: "Verify the complete system achieves COP ≥ 4.5 with room temperature within 2°C of setpoint.",
        detailedGuidance: "This confirms the system is properly designed. Document all parameters for the client report.",
        successIndicator: "COP ≥ 4.5 and temperature stable."
      }
    ],
    completionCriteria: {
      description: "Complete professional system assessment",
      conditions: [
        "COP ≥ 4.5",
        "Room temperature within 2°C of setpoint"
      ]
    },
    knowledgeSynthesis: {
      keyTakeaways: [
        "Professional assessment covers all system aspects",
        "Envelope, ventilation, emitters, and controls all matter",
        "COP 4.5+ at design conditions is achievable with optimization",
        "Comprehensive analysis justifies premium consulting fees"
      ],
      formulaHighlight: "COP_system = f(envelope, ventilation, T_supply, emitters, controls)\nROI = Annual_savings × years / Investment_cost",
      practicalTip: "Prepare a professional report template that shows: current state, recommended improvements, expected COP, annual savings, payback period, and 20-year benefit analysis."
    },
    isChallenge: true
  }
];

// ============================================================================
// SALES PERSON QUESTS
// ============================================================================

export const SALES_PERSON_QUESTS = [
  SYSTEM_FAMILIARIZATION_QUEST,
  {
    id: 2,
    title: "Understanding Heat Pump Efficiency",
    category: "Product Knowledge",
    icon: <ThunderboltOutlined />,
    difficulty: "Beginner",
    estimatedTime: "8 minutes",
    learningObjectives: [
      "Explain COP in customer-friendly terms",
      "Demonstrate the efficiency advantage of heat pumps",
      "Convert technical specs into customer benefits"
    ],
    scenario: {
      context: "A customer asks 'Why is a heat pump better than my gas boiler?' You need to clearly demonstrate the efficiency advantage in terms they understand.",
      realWorldConnection: "Sales success depends on explaining technical advantages in simple, benefit-focused language. COP is your key selling point."
    },
    steps: [
      {
        id: "2a",
        task: "Start the simulation",
        instruction: "Start the simulation so you can show the customer real-time operation.",
        detailedGuidance: "Live demonstration is more convincing than static slides. The simulation helps customers visualize the technology.",
        successIndicator: "Simulation is running."
      },
      {
        id: "2b",
        task: "Point out the COP display",
        instruction: "Find the COP value on the external unit. This is your key efficiency metric to explain.",
        detailedGuidance: "COP (Coefficient of Performance) shows how much heat you get for the electricity used. COP of 4 means 4 kWh heat for every 1 kWh electricity.",
        successIndicator: "You can locate and read the COP value."
      },
      {
        id: "2c",
        task: "Explain in customer terms",
        instruction: "Practice explaining: 'For every €1 of electricity, this system delivers €3-4 worth of heat. A gas boiler gives you less than €1 of heat for each €1 of gas.'",
        detailedGuidance: "Customers understand money better than technical units. Converting COP to cost savings makes the benefit tangible.",
        successIndicator: "You can explain COP in benefit terms."
      },
      {
        id: "2d",
        task: "Demonstrate conditions affect efficiency",
        instruction: "Show how COP changes with different conditions — it's higher in milder weather, giving even better value most of the year.",
        detailedGuidance: "Many customers worry about cold weather performance. Show them that COP is lowest at -10°C (design conditions) but higher (5.0+) during milder months when most heating happens.",
        successIndicator: "You can show COP variation with conditions."
      }
    ],
    completionCriteria: {
      description: "Demonstrate heat pump efficiency",
      conditions: ["Simulation must be running"]
    },
    knowledgeSynthesis: {
      keyTakeaways: [
        "COP = heat output / electricity input — the efficiency ratio",
        "COP 4 means 4× more heat than electricity used",
        "Gas boiler efficiency is <100%, heat pump is 300-500%",
        "COP is higher in mild weather — great for most of the heating season"
      ],
      formulaHighlight: "COP 4 = 400% efficiency compared to direct electric heating",
      practicalTip: "Keep a simple cost comparison ready: 'If gas costs €0.10/kWh and electricity €0.30/kWh, COP 4 means effective heating cost of €0.075/kWh heat — cheaper than gas!'"
    }
  },
  {
    id: 3,
    title: "Comfort & Control Benefits",
    category: "Customer Value",
    icon: <HeartOutlined />,
    difficulty: "Beginner",
    estimatedTime: "8 minutes",
    learningObjectives: [
      "Demonstrate temperature stability and comfort",
      "Show customers how the system maintains ideal conditions",
      "Explain the 'set and forget' operation"
    ],
    scenario: {
      context: "A customer is concerned about comfort — 'Will a heat pump keep my house as warm as my boiler?' You need to demonstrate reliable, stable heating.",
      realWorldConnection: "Comfort concerns are common objections. Showing stable operation builds confidence and addresses fears."
    },
    steps: [
      {
        id: "3a",
        task: "Set comfortable conditions",
        instruction: "Configure the room for Living Room (20°C target) and start the simulation.",
        detailedGuidance: "Living room at 20°C is the most common customer scenario. Show the system achieving and maintaining this temperature.",
        successIndicator: "Room type is Living Room with 20°C target."
      },
      {
        id: "3b",
        task: "Demonstrate temperature stability",
        instruction: "Let the simulation run and show how room temperature stays within 1-2°C of the setpoint.",
        detailedGuidance: "Heat pumps often provide more stable temperatures than boilers because they modulate rather than cycle on/off dramatically.",
        successIndicator: "Room temperature is stable near setpoint."
      },
      {
        id: "3c",
        task: "Show the thermostat control",
        instruction: "Point out the thermostat valve on the radiator that automatically adjusts to maintain comfort.",
        detailedGuidance: "The thermostat continuously adjusts to maintain the set temperature. No cold spots, no overheating — just consistent comfort.",
        successIndicator: "You can explain the thermostat function."
      },
      {
        id: "3d",
        task: "Try different room types",
        instruction: "Show how different room types (bathroom 24°C, bedroom 18°C) have appropriate temperatures.",
        detailedGuidance: "This demonstrates that heat pump systems can provide zoned comfort throughout the home — each room at its ideal temperature.",
        successIndicator: "Room type is 'Living Room' and target is 20°C."
      }
    ],
    completionCriteria: {
      description: "Show comfort and control",
      conditions: [
        "Room type set to 'Living Room'",
        "Target temperature = 20°C"
      ]
    },
    knowledgeSynthesis: {
      keyTakeaways: [
        "Heat pumps provide stable, consistent heating",
        "Thermostat valves maintain comfort automatically",
        "Different rooms can have different target temperatures",
        "Modern systems are 'set and forget' easy"
      ],
      formulaHighlight: "Comfort = stable temperature ± 1-2°C of setpoint",
      practicalTip: "When a customer asks 'Is it complicated?', answer: 'Set your preferred temperature once, and the system handles everything automatically. Most customers never touch it again.'"
    }
  },
  {
    id: 4,
    title: "Addressing Building Concerns",
    category: "Objection Handling",
    icon: <HomeOutlined />,
    difficulty: "Intermediate",
    estimatedTime: "10 minutes",
    learningObjectives: [
      "Address 'My house is too old for a heat pump' objection",
      "Show how insulation affects system performance",
      "Position envelope upgrades as added value"
    ],
    scenario: {
      context: "A customer says 'I've heard heat pumps only work in new houses. Mine is 50 years old.' You need to address this common misconception.",
      realWorldConnection: "Building age objections are very common. Being able to show the real factors builds credibility and opens sales opportunities."
    },
    steps: [
      {
        id: "4a",
        task: "Open envelope parameters",
        instruction: "Click 'U-Parameters' to show the building envelope settings.",
        detailedGuidance: "This shows customers that what matters is insulation quality, not building age. Old buildings can be upgraded.",
        successIndicator: "U-Parameters dialog is open."
      },
      {
        id: "4b",
        task: "Show poorly insulated scenario",
        instruction: "Set wall U-value to 1.0 W/(m²·K) (old, uninsulated wall) and show the high heat losses.",
        detailedGuidance: "Old buildings with U=1.0+ have high heat losses. The simulation shows this creates a challenge — but not an impossibility.",
        successIndicator: "You can show the impact of poor insulation."
      },
      {
        id: "4c",
        task: "Demonstrate improvement impact",
        instruction: "Improve wall U-value to 0.2 W/(m²·K) and show how heat losses drop dramatically.",
        detailedGuidance: "This shows that insulation (often eligible for grants) transforms heat pump viability. The house age becomes irrelevant.",
        successIndicator: "Wall U-value is approximately 0.2 W/(m²·K)."
      },
      {
        id: "4d",
        task: "Enable heat flux visualization",
        instruction: "Toggle heat flux overlay to visually show where heat escapes.",
        detailedGuidance: "Visual demonstration is powerful. Show how improved insulation stops the heat flowing out — the arrows get smaller.",
        successIndicator: "Heat flux overlay is active."
      }
    ],
    completionCriteria: {
      description: "Address building quality concerns",
      conditions: [
        "Wall area between 48-52 m²",
        "Wall U-value between 0.18-0.22 W/(m²·K)"
      ]
    },
    knowledgeSynthesis: {
      keyTakeaways: [
        "Building age doesn't determine heat pump suitability — insulation does",
        "Old buildings can be upgraded with insulation",
        "Better insulation means smaller, cheaper heat pump needed",
        "Package deals (insulation + heat pump) are compelling customer offers"
      ],
      formulaHighlight: "Better insulation = Lower heat loss = More efficient heat pump operation",
      practicalTip: "Turn the objection into an opportunity: 'Your older home could benefit from an insulation package that makes a heat pump work perfectly AND reduces your heating needs by 50%.'"
    }
  },
  {
    id: 5,
    title: "Running Cost Demonstration",
    category: "Value Selling",
    icon: <DollarOutlined />,
    difficulty: "Intermediate",
    estimatedTime: "12 minutes",
    learningObjectives: [
      "Demonstrate operating cost savings visually",
      "Show how lower supply temperature saves money",
      "Create compelling cost comparisons"
    ],
    scenario: {
      context: "A customer says 'Heat pumps use expensive electricity. How do I know I'll save money?' You need to demonstrate the cost advantage clearly.",
      realWorldConnection: "Cost objections are universal. Being able to show real numbers builds trust and closes sales."
    },
    steps: [
      {
        id: "5a",
        task: "Set baseline conditions",
        instruction: "Configure -10°C outdoor, 20°C living room — the standard comparison conditions.",
        detailedGuidance: "Use standard conditions so your examples are consistent and credible. Design conditions show worst-case performance.",
        successIndicator: "Reference conditions are set."
      },
      {
        id: "5b",
        task: "Show high-temp operation",
        instruction: "Set supply temperature to 55°C and note the COP (around 2.5-3.0).",
        detailedGuidance: "This represents an un-optimized system similar to competitor quotes that don't consider efficiency.",
        successIndicator: "Supply temperature is 55°C."
      },
      {
        id: "5c",
        task: "Optimize for efficiency",
        instruction: "Lower supply temperature to 40°C, resize radiators if needed, and show COP improving to 4.0+.",
        detailedGuidance: "This is your competitive advantage. Show how proper system design dramatically improves efficiency. COP 4 vs COP 3 is 25% less electricity!",
        successIndicator: "Supply temperature ≤ 40°C with COP 4+."
      },
      {
        id: "5d",
        task: "Calculate annual savings",
        instruction: "Present the cost comparison: 'At COP 4, heating costs around €0.075/kWh. Gas is €0.10/kWh. That's 25% savings!'",
        detailedGuidance: "Have local energy prices ready. Customize the calculation to the customer's situation for maximum impact.",
        successIndicator: "You can present a cost comparison."
      },
      {
        id: "5e",
        task: "Demonstrate mild weather benefit",
        instruction: "Set outdoor temperature to 5°C and show COP rising to 5.0+.",
        detailedGuidance: "Most heating happens in mild weather (5-10°C), not at -10°C. During these times, COP is even higher, averaging better performance.",
        successIndicator: "You can show COP improvement in milder conditions."
      }
    ],
    completionCriteria: {
      description: "Demonstrate cost-effective operation",
      conditions: [
        "Supply temperature ≤ 40°C",
        "Heat output ≥ 90% of total heat loss"
      ]
    },
    knowledgeSynthesis: {
      keyTakeaways: [
        "COP converts technical efficiency to cost terms",
        "Lower supply temperature = lower running costs",
        "Most heating happens in mild weather where COP is highest",
        "Annual average COP is often better than worst-case design COP"
      ],
      formulaHighlight: "Cost per kWh heat = Electricity price / COP\nAt COP 4 with €0.30/kWh electricity = €0.075/kWh heat",
      practicalTip: "Prepare a one-page cost comparison specific to your area. Show gas vs heat pump over 10 years including carbon savings. Make the numbers real and local."
    }
  },
  {
    id: 6,
    title: "Complete Customer Presentation",
    category: "Sales Mastery",
    icon: <TrophyOutlined />,
    difficulty: "Advanced",
    estimatedTime: "15 minutes",
    learningObjectives: [
      "Deliver a complete, convincing customer demonstration",
      "Address all common objections proactively",
      "Achieve 'wow factor' with COP 4.5+ demonstration"
    ],
    scenario: {
      context: "You're meeting a qualified prospect ready to make a decision. This is your chance to deliver a compelling demonstration that covers efficiency, comfort, and value.",
      realWorldConnection: "This scenario represents the skills needed to close a sale confidently. Master this and you'll convert more prospects to customers."
    },
    steps: [
      {
        id: "6a",
        task: "Start with comfort",
        instruction: "Set Living Room at 20°C and demonstrate stable, comfortable heating.",
        detailedGuidance: "Start with what customers care most about — comfort. Show the system maintains temperature reliably.",
        successIndicator: "Comfort demonstration is complete."
      },
      {
        id: "6b",
        task: "Show building adaptability",
        instruction: "Demonstrate how the system adapts to different building types by adjusting envelope parameters.",
        detailedGuidance: "Address the 'does it work with my house' concern early. Show flexibility for different building standards.",
        successIndicator: "Building envelope flexibility is demonstrated."
      },
      {
        id: "6c",
        task: "Demonstrate ventilation awareness",
        instruction: "Set appropriate ventilation (ACH < 1.25) and show the system handles real building conditions.",
        detailedGuidance: "Professional knowledge of ventilation impacts builds credibility. Mention HRV if the customer has a modern, tight building.",
        successIndicator: "Ventilation is set appropriately."
      },
      {
        id: "6d",
        task: "Optimize for maximum efficiency",
        instruction: "Set supply temperature ≤ 40°C with properly sized emitters to achieve COP 4.5+.",
        detailedGuidance: "This is your 'wow' moment. COP 4.5 sounds impressive and translates to real cost savings. Make sure to explain the benefit.",
        successIndicator: "COP ≥ 4.5."
      },
      {
        id: "6e",
        task: "Close with value proposition",
        instruction: "Verify room temperature is stable (within 2°C of target) and summarize: efficiency, comfort, and value.",
        detailedGuidance: "End with a clear summary: 'This system delivers over 4 kWh of heat for every 1 kWh of electricity, maintains your home at exactly the temperature you want, and will save you money every year. Are you ready to proceed?'",
        successIndicator: "Complete demonstration with COP 4.5+ and stable temperature."
      }
    ],
    completionCriteria: {
      description: "Master sales presentation",
      conditions: [
        "COP ≥ 4.5",
        "Room temperature within 2°C of setpoint"
      ]
    },
    knowledgeSynthesis: {
      keyTakeaways: [
        "Start with comfort, move to efficiency, close with value",
        "Address objections proactively with demonstrations",
        "COP 4.5+ is your 'wow factor' — make it memorable",
        "Always summarize benefits in customer terms, not technical specs"
      ],
      formulaHighlight: "Sales success = Comfort proof + Efficiency demonstration + Value proposition",
      practicalTip: "Practice this demonstration until it's smooth and natural. Know the common questions and have answers ready. Confidence sells!"
    },
    isChallenge: true
  }
];

// ============================================================================
// USER TYPE CONFIGURATION
// ============================================================================

export const USER_TYPES = {
  installer: {
    id: "installer",
    label: "Installer",
    icon: <ToolOutlined />,
    description: "HVAC technicians and system installers",
    quests: INSTALLER_QUESTS
  },
  energyConsultant: {
    id: "energyConsultant",
    label: "Energy Consultant",
    icon: <CalculatorOutlined />,
    description: "Energy advisors and building assessors",
    quests: ENERGY_CONSULTANT_QUESTS
  },
  salesPerson: {
    id: "salesPerson",
    label: "Sales Professional",
    icon: <TeamOutlined />,
    description: "Heat pump sales and customer advisors",
    quests: SALES_PERSON_QUESTS
  }
};

const BUILTIN_QUESTS_BY_USER_TYPE = {
  installer: INSTALLER_QUESTS,
  energyConsultant: ENERGY_CONSULTANT_QUESTS,
  salesPerson: SALES_PERSON_QUESTS,
};

const isGermanQuestLanguage = (language = "") => String(language).toLowerCase().startsWith("de");

const mergeStepOverrides = (steps = [], overrides = []) => {
  const overridesById = new Map(overrides.map((step) => [step.id, step]));

  return steps.map((step) => {
    const stepOverride = overridesById.get(step.id);
    return stepOverride ? { ...step, ...stepOverride } : step;
  });
};

const mergeQuestOverride = (quest, override) => ({
  ...quest,
  ...override,
  learningObjectives: override.learningObjectives || quest.learningObjectives,
  scenario: override.scenario ? { ...quest.scenario, ...override.scenario } : quest.scenario,
  steps: override.steps ? mergeStepOverrides(quest.steps, override.steps) : quest.steps,
  completionCriteria: override.completionCriteria
    ? {
        ...quest.completionCriteria,
        ...override.completionCriteria,
        conditions: override.completionCriteria.conditions || quest.completionCriteria.conditions,
      }
    : quest.completionCriteria,
  knowledgeSynthesis: override.knowledgeSynthesis
    ? {
        ...quest.knowledgeSynthesis,
        ...override.knowledgeSynthesis,
        keyTakeaways: override.knowledgeSynthesis.keyTakeaways || quest.knowledgeSynthesis.keyTakeaways,
      }
    : quest.knowledgeSynthesis,
});

const mergeQuestOverrides = (quests = [], overrides = []) => {
  const overridesById = new Map(overrides.map((quest) => [quest.id, quest]));

  return quests.map((quest) => {
    const questOverride = overridesById.get(quest.id);
    return questOverride ? mergeQuestOverride(quest, questOverride) : quest;
  });
};

/**
 * Get quests for a specific user type
 * @param {string} userType - One of: installer, energyConsultant, salesPerson
 * @param {string} language - Active UI language code
 * @returns {Array} Array of quest objects
 */
export const getQuestsForUserType = (userType, language) => {
  const quests = BUILTIN_QUESTS_BY_USER_TYPE[userType] || INSTALLER_QUESTS;

  if (!isGermanQuestLanguage(language)) {
    return quests;
  }

  return mergeQuestOverrides(quests, [
    ...GERMAN_SHARED_QUEST_OVERRIDES,
    ...(GERMAN_USER_TYPE_QUEST_OVERRIDES[userType] || GERMAN_USER_TYPE_QUEST_OVERRIDES.installer),
  ]);
};

/**
 * Get user type configuration
 * @param {string} userType - One of: installer, energyConsultant, salesPerson
 * @returns {Object} User type configuration
 */
export const getUserTypeConfig = (userType) => {
  return USER_TYPES[userType] || USER_TYPES.installer;
};

export default USER_TYPES;
